import { useState, useEffect, useCallback } from 'react'
import { adminService } from '../../services/adminService'
import {
  Landmark,
  Plus,
  Search,
  X,
  AlertTriangle,
  Loader2,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  BarChart3,
  TrendingUp,
  Users,
  Activity,
} from 'lucide-react'

// ─── Types ─────────────────────────────────────────────────────────────────────

interface Lender {
  id: string
  name: string
  email: string
  subscriptionStatus: string
  accessLimit: number
  usageCount: number
  createdAt: string
}

interface PaginatedResponse {
  items: Lender[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

interface LenderAnalytics {
  totalLenders: number
  activeLenders: number
  totalUsage: number
  averageUsage: number
  topLenders: { name: string; usageCount: number }[]
  usageTrend: { date: string; count: number }[]
}

// ─── Constants ─────────────────────────────────────────────────────────────────

const SUBSCRIPTION_STATUSES = [
  { value: '', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'trial', label: 'Trial' },
  { value: 'expired', label: 'Expired' },
  { value: 'suspended', label: 'Suspended' },
]

const PAGE_SIZES = [10, 25, 50, 100]

// ─── Component ─────────────────────────────────────────────────────────────────

export function AdminLenders() {
  // Data state
  const [lenders, setLenders] = useState<Lender[]>([])
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Analytics state
  const [analytics, setAnalytics] = useState<LenderAnalytics | null>(null)
  const [analyticsLoading, setAnalyticsLoading] = useState(true)

  // Pagination
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  // Filters
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')

  // Create modal state
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [createLoading, setCreateLoading] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const [createForm, setCreateForm] = useState({ name: '', email: '' })

  // ─── Data Fetching ─────────────────────────────────────────────────────────

  const fetchLenders = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params: Record<string, any> = { page, pageSize }
      if (search) params.search = search
      if (statusFilter) params.subscriptionStatus = statusFilter

      const response = await adminService.listLenders(params)
      const data = response.data

      // Handle both array response and paginated response
      const lenderList: Lender[] = (Array.isArray(data) ? data : (data as PaginatedResponse).items || []).map((item: any) => ({
        id: item.id,
        name: item.name,
        email: item.email,
        subscriptionStatus: item.subscriptionStatus || item.status || 'active',
        accessLimit: item.accessLimit ?? item.apiRateLimit ?? item.monthlyLookups ?? 0,
        usageCount: item.usageCount ?? item.monthlyLookups ?? 0,
        createdAt: item.createdAt,
      }))

      setLenders(lenderList)

      if (Array.isArray(data)) {
        setTotal(lenderList.length)
        setTotalPages(1)
      } else {
        setTotal((data as PaginatedResponse).total || lenderList.length)
        setTotalPages((data as PaginatedResponse).totalPages || 1)
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load lenders')
      setLenders([])
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, search, statusFilter])

  const fetchAnalytics = useCallback(async () => {
    setAnalyticsLoading(true)
    try {
      const response = await adminService.getLenderAnalytics()
      const data = response.data
      
      // The API returns an array of per-lender analytics — compute summary
      if (Array.isArray(data)) {
        const totalLenders = data.length
        const activeLenders = data.filter((l: any) => l.totalLookups > 0 || l.lastActivityAt).length
        const totalUsage = data.reduce((sum: number, l: any) => sum + (l.totalLookups || 0), 0)
        const averageUsage = totalLenders > 0 ? Math.round(totalUsage / totalLenders) : 0
        const topLenders = [...data]
          .sort((a: any, b: any) => (b.totalLookups || 0) - (a.totalLookups || 0))
          .slice(0, 5)
          .map((l: any) => ({ name: l.lenderName, usageCount: l.totalLookups || 0 }))
        
        setAnalytics({ totalLenders, activeLenders, totalUsage, averageUsage, topLenders, usageTrend: [] })
      } else {
        setAnalytics(data as LenderAnalytics)
      }
    } catch {
      // Analytics are non-critical — silently fail
      setAnalytics(null)
    } finally {
      setAnalyticsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchLenders()
  }, [fetchLenders])

  useEffect(() => {
    fetchAnalytics()
  }, [fetchAnalytics])

  // ─── Filter Handlers ───────────────────────────────────────────────────────

  const handleSearchChange = (value: string) => {
    setSearch(value)
    setPage(1)
  }

  const handleStatusChange = (value: string) => {
    setStatusFilter(value)
    setPage(1)
  }

  const clearFilters = () => {
    setSearch('')
    setStatusFilter('')
    setPage(1)
  }

  const hasActiveFilters = search || statusFilter

  // ─── Create Lender ─────────────────────────────────────────────────────────

  const openCreateModal = () => {
    setCreateForm({ name: '', email: '' })
    setCreateError(null)
    setShowCreateModal(true)
  }

  const closeCreateModal = () => {
    setShowCreateModal(false)
    setCreateError(null)
  }

  const handleCreateLender = async () => {
    if (!createForm.name.trim() || !createForm.email.trim()) {
      setCreateError('Name and email are required')
      return
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(createForm.email)) {
      setCreateError('Please enter a valid email address')
      return
    }
    setCreateLoading(true)
    setCreateError(null)
    try {
      await adminService.createLender({
        name: createForm.name.trim(),
        email: createForm.email.trim(),
      })
      closeCreateModal()
      fetchLenders()
      fetchAnalytics()
    } catch (err: any) {
      setCreateError(err.message || 'Failed to create lender')
    } finally {
      setCreateLoading(false)
    }
  }

  // ─── Pagination Helpers ────────────────────────────────────────────────────

  const getPageNumbers = (): (number | '...')[] => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1)
    }
    const pages: (number | '...')[] = [1]
    if (page > 3) pages.push('...')
    const start = Math.max(2, page - 1)
    const end = Math.min(totalPages - 1, page + 1)
    for (let i = start; i <= end; i++) pages.push(i)
    if (page < totalPages - 2) pages.push('...')
    pages.push(totalPages)
    return pages
  }

  // ─── Render Helpers ────────────────────────────────────────────────────────

  const getSubscriptionBadge = (status: string) => {
    const colorMap: Record<string, string> = {
      active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      trial: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      expired: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
      suspended: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
    }
    return colorMap[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
  }

  const formatNumber = (n: number | null | undefined) => {
    return (n ?? 0).toLocaleString()
  }

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Landmark className="h-7 w-7 text-blue-600 dark:text-blue-400" />
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
              Lender Management
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Manage lender accounts, subscriptions, and usage analytics
            </p>
          </div>
        </div>
        <button
          onClick={openCreateModal}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
        >
          <Plus className="h-4 w-4" />
          Create Lender
        </button>
      </div>

      {/* Analytics Section */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {analyticsLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 animate-pulse"
            >
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-2" />
              <div className="h-7 bg-gray-200 dark:bg-gray-700 rounded w-16" />
            </div>
          ))
        ) : analytics ? (
          <>
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
              <div className="flex items-center gap-2 mb-1">
                <Users className="h-4 w-4 text-blue-500" />
                <span className="text-sm text-gray-500 dark:text-gray-400">Total Lenders</span>
              </div>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {formatNumber(analytics.totalLenders)}
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
              <div className="flex items-center gap-2 mb-1">
                <Activity className="h-4 w-4 text-green-500" />
                <span className="text-sm text-gray-500 dark:text-gray-400">Active Lenders</span>
              </div>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {formatNumber(analytics.activeLenders)}
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
              <div className="flex items-center gap-2 mb-1">
                <BarChart3 className="h-4 w-4 text-purple-500" />
                <span className="text-sm text-gray-500 dark:text-gray-400">Total Usage</span>
              </div>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {formatNumber(analytics.totalUsage)}
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="h-4 w-4 text-orange-500" />
                <span className="text-sm text-gray-500 dark:text-gray-400">Avg Usage / Lender</span>
              </div>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {formatNumber(analytics.averageUsage)}
              </p>
            </div>
          </>
        ) : null}
      </div>

      {/* Top Lenders (if analytics available) */}
      {analytics && analytics.topLenders && analytics.topLenders.length > 0 && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
          <h2 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Top Lenders by Usage
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {analytics.topLenders.slice(0, 5).map((lender, i) => (
              <div
                key={i}
                className="flex items-center justify-between px-3 py-2 rounded-md bg-gray-50 dark:bg-gray-700/50"
              >
                <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
                  {lender.name}
                </span>
                <span className="text-sm font-medium text-blue-600 dark:text-blue-400 ml-2">
                  {formatNumber(lender.usageCount)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Search */}
          <div className="relative lg:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => handleStatusChange(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {SUBSCRIPTION_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="inline-flex items-center justify-center gap-1 px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <X className="h-3.5 w-3.5" />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="flex items-center gap-3 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          <button
            onClick={fetchLenders}
            className="ml-auto inline-flex items-center gap-1 text-sm text-red-700 dark:text-red-300 hover:underline"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Retry
          </button>
        </div>
      )}

      {/* Lenders Table */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600 dark:text-blue-400" />
            <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">
              Loading lenders...
            </span>
          </div>
        ) : lenders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <Landmark className="h-12 w-12 text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              {hasActiveFilters
                ? 'No lenders match the current filters'
                : 'No lenders found'}
            </p>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="mt-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">
                      Name
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">
                      Email
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">
                      Subscription
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">
                      Access Limit
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">
                      Usage
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {lenders.map((lender) => (
                    <tr
                      key={lender.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                    >
                      <td className="px-4 py-3 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                        {lender.name}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                        {lender.email}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getSubscriptionBadge(lender.subscriptionStatus)}`}
                        >
                          {lender.subscriptionStatus}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                        {formatNumber(lender.accessLimit)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-900 dark:text-white font-medium">
                            {formatNumber(lender.usageCount)}
                          </span>
                          {lender.accessLimit > 0 && (
                            <div className="flex-1 max-w-[80px]">
                              <div className="h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                                <div
                                  className={`h-full rounded-full transition-all ${
                                    lender.usageCount / lender.accessLimit > 0.9
                                      ? 'bg-red-500'
                                      : lender.usageCount / lender.accessLimit > 0.7
                                        ? 'bg-yellow-500'
                                        : 'bg-blue-500'
                                  }`}
                                  style={{
                                    width: `${Math.min(100, (lender.usageCount / lender.accessLimit) * 100)}%`,
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <span>
                  Showing {(page - 1) * pageSize + 1}–
                  {Math.min(page * pageSize, total)} of {total}
                </span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value))
                    setPage(1)
                  }}
                  className="ml-2 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {PAGE_SIZES.map((size) => (
                    <option key={size} value={size}>
                      {size} / page
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="p-1.5 rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                {getPageNumbers().map((p, i) =>
                  p === '...' ? (
                    <span
                      key={`ellipsis-${i}`}
                      className="px-2 text-gray-400 dark:text-gray-500"
                    >
                      ...
                    </span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                        p === page
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      {p}
                    </button>
                  )
                )}
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="p-1.5 rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Create Lender Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={closeCreateModal}
          />
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Create Lender
              </h2>
              <button
                onClick={closeCreateModal}
                className="p-1 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {createError && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                  <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400 flex-shrink-0" />
                  <p className="text-sm text-red-700 dark:text-red-300">{createError}</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={createForm.name}
                  onChange={(e) =>
                    setCreateForm((f) => ({ ...f, name: e.target.value }))
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Lender name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={createForm.email}
                  onChange={(e) =>
                    setCreateForm((f) => ({ ...f, email: e.target.value }))
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="lender@example.com"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-3">
              <button
                onClick={closeCreateModal}
                disabled={createLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateLender}
                disabled={createLoading}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                {createLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                Create Lender
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
